import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CommonService } from './common.service';
import { ColIdEnum } from './reports.service';
import { TranslationsService } from './translations.service';

export interface RichToolTip {
  topOffset?: string;
  leftOffset?: string;
  maxWidth?: string;
}

export interface TooltipContent {
  title?: string;
  description?: string;
  richToolTip?: RichToolTip;
}

interface MvrSvcDetail {
  code: string;
  convictionDate?: string;
  description?: string;
  eventDate?: string;
}

interface LicenceClassDetail {
  code: string;
  commercialStatus?: string;
  dateExpires: string;
  dateIssued: string;
  description?: string;
  restrictions?: string;
  status?: string;
  type?: string;
}

interface LicenceEndorsementDetail {
  code: string;
  offenceDate: string;
  convictionDate: string;
  description: string;
}

interface LicenceTypeDetail {
  code: string;
  description: string;
}

interface TachographCardStatusDetail {
  code: string;
}

interface MvrStatusDetail {
  code: string;
  errorDetail?: string;
}

@Injectable({ providedIn: 'root' })
export class TooltipService {
  public isVisible = new BehaviorSubject<boolean>(false);
  public position$ = new BehaviorSubject<{ x: number; y: number }>({ x: 0, y: 0 });
  public content$ = new BehaviorSubject<TooltipContent>({ title: '', description: '' });

  private tooltipTranslationPhrases: Array<string> = ['ACCIDENT', 'Event Date'];

  constructor(private cService: CommonService, private translationsService: TranslationsService) {}

  /**
   * Maps column ID's to their data build method
   */
  private columnMethodMapping = {
    [ColIdEnum.mvrSvcCodes]: this.getMvrSvcDetail,
    [ColIdEnum.mvrLicenseClass]: this.getLicenceClassDetail,
    [ColIdEnum.endorsements]: this.getEndorsementsDetail,
    [ColIdEnum.licenceType]: this.getLicenceTypeDetail,
    [ColIdEnum.cardStatus]: this.getTachographCardStatusDetail,
    [ColIdEnum.mvrStatus]: this.getMvrStatusDetail,
  };

  /**
   * Displays the tooltip
   */
  public display(): void {
    this.isVisible.next(true);
  }

  /**
   * Hides the tooltip
   */
  public hide(): void {
    this.isVisible.next(false);
  }

  /**
   * Positions the tooltip relative to the element.
   */
  public positionRelativeTo(targetElement: Element): void {
    const bounds = targetElement.getBoundingClientRect();

    if (this.cService.selectedLanguage && this.cService.selectedLanguage.isRtl) {
      this.position$.next({ x: bounds.right, y: bounds.top });
    } else {
      this.position$.next({ x: bounds.left, y: bounds.top });
    }
  }

  /**
   * Displays and positions the tooltip relative to the element.
   */
  public displayRelativeTo(targetElement: Element): void {
    this.display();
    this.positionRelativeTo(targetElement);
  }

  /**
   * Sets the content for the popup
   */
  public setContent(data: TooltipContent): void {
    this.content$.next(data);
  }

  /**
   * Takes the column name and data, and uses the appropriate method to
   * build the required data.
   */
  public buildData(colId: string, sourceData: unknown): TooltipContent {
    const colMethod = this.columnMethodMapping[colId];
    return colMethod ? colMethod.apply(this, [sourceData]) : null;
  }

  /**
   * Builds the data for the MVR SVC column.
   */
  private getMvrSvcDetail(data: MvrSvcDetail): TooltipContent {
    let description = '';
    let title = data.code;

    if (data.description) {
      title += `: ${data.description}`;
    }

    if (data.eventDate) {
      description += `${this.cService.readyTranslations.licenceChecking.eventDate}: ${data.eventDate}. `;
    }

    if (data.convictionDate) {
      description += `${this.cService.readyTranslations.licenceChecking.convictionDate}: ${data.convictionDate}. `;
    }

    return {
      title: title,
      description: description,
    };
  }

  /**
   * Builds the data for the MVR Licence Class column.
   */
  private getLicenceClassDetail(data: LicenceClassDetail): TooltipContent {
    let description = '';
    const title = `${this.cService.readyTranslations.licenceChecking.licenceClass} ${data.code}`;

    if (data.description) {
      description += `${data.description} `;
    }

    if (data.type) {
      description += `${this.cService.readyTranslations.licenceChecking.licenceType}: ${data.type}. `;
    }

    if (data.dateIssued) {
      description += `${this.cService.readyTranslations.licenceChecking.dateIssued}: ${data.dateIssued}. `;
    }

    if (data.dateExpires) {
      description += `${this.cService.readyTranslations.licenceChecking.dateExpires}: ${data.dateExpires}. `;
    }

    if (data.restrictions) {
      description += `${this.cService.readyTranslations.licenceChecking.restrictions}: ${data.restrictions}. `;
    }

    if (data.status) {
      description += `${this.cService.readyTranslations.licenceChecking.status}: ${data.status}. `;
    }

    if (data.commercialStatus) {
      description += `${this.cService.readyTranslations.licenceChecking.commercialStatus}: ${data.commercialStatus}. `;
    }

    return {
      title: title,
      description: description,
    };
  }

  /**
   * Builds the data for the endorsements tooltip
   */
  private getEndorsementsDetail(data: LicenceEndorsementDetail): TooltipContent {
    let description = '';

    if (data.description) {
      description += `${data.description}. `;
    }

    if (data.offenceDate) {
      description += `${this.cService.readyTranslations.licenceChecking.offenceDate}: ${data.offenceDate}. `;
    }

    if (data.convictionDate) {
      description += `${this.cService.readyTranslations.licenceChecking.convictionDate}: ${data.convictionDate}. `;
    }

    return {
      title: data.code,
      description: description,
    };
  }

  /**
   * Builds the data for the Mvr Status tooltip
   */
  private getMvrStatusDetail(data: MvrStatusDetail): TooltipContent {
    return {
      title: this.cService.readyTranslations.generic.fullError,
      description: data.errorDetail,
    };
  }

  /**
   * Builds the data for the endorsements tooltip
   */
  private getLicenceTypeDetail(data: LicenceTypeDetail): TooltipContent {
    return {
      title: data.code,
      description: this.translationsService.translate(`LICENCE_CHECKING.${data.description}`, data.description),
    };
  }

  /**
   * Builds the data for the tachograph card status tooltip
   */
  private getTachographCardStatusDetail(data: TachographCardStatusDetail): TooltipContent {
    return {
      title: this.cService.readyTranslations.tachographStatus.cardStatus,
      description: this.cService.readyTranslations.tachographStatus['code' + data.code],
    };
  }

  public getTooltipByID(tooltipID: any): TooltipContent {
    const tooltipIDEnum = {
      readingTypeChanged: 'readingTypeChanged',
      readingSubmissionInconsistent: 'readingSubmissionInconsistent',
      totalDistanceOnlyIncludesReported: 'totalDistanceOnlyIncludesReported',
    };

    let tooltipContent = {};

    if (tooltipID === tooltipIDEnum.readingTypeChanged) {
      tooltipContent = {
        title: this.cService.readyTranslations.messagesAndWarnings.unableToCalculate,
        description:
          this.cService.readyTranslations.messagesAndWarnings
            .unableToCalculateReasonDriverChangedFromDistanceToOdometerReading,
      };
    } else if (tooltipID === tooltipIDEnum.readingSubmissionInconsistent) {
      tooltipContent = {
        title: this.cService.readyTranslations.messagesAndWarnings.unableToCalculate,
        description:
          this.cService.readyTranslations.messagesAndWarnings
            .unableToCalculateReasonPossibleInconsistentReadingSubmitted,
      };
    } else if (tooltipID === tooltipIDEnum.totalDistanceOnlyIncludesReported) {
      tooltipContent = {
        title: this.cService.readyTranslations.messagesAndWarnings.unableToFullyCalculate,
        description:
          this.cService.readyTranslations.messagesAndWarnings.unableToCalculateReasonTotalDistanceOnlyIncludesReported,
      };
    }

    return tooltipContent;
  }

  attemptTooltipContentTranslation(content: TooltipContent): TooltipContent {
    const translatedContent: TooltipContent = content;

    if (content.title && content.description) {
      this.tooltipTranslationPhrases.forEach((phrase: string) => {
        if (this.cService.readyTranslations.customTooltips[phrase]) {
          const phraseSearch = new RegExp(phrase, 'gi');

          if (content.title.includes(phrase)) {
            translatedContent.title = translatedContent.title.replace(
              phraseSearch,
              this.cService.readyTranslations.customTooltips[phrase]
            );
          }

          if (content.description.includes(phrase)) {
            translatedContent.description = translatedContent.description.replace(
              phraseSearch,
              this.cService.readyTranslations.customTooltips[phrase]
            );
          }
        }
      });
    }

    return translatedContent;
  }
}
